import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ConvertISONumberToNormal, getFormData } from 'src/app/shared/helpers/utils';
import { NgbActiveModal, NgbDate, NgbDatepickerConfig, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { WithdrawFundsFormProvider } from '../../withdrawFunds-form-provider';
import { WithdrawFundsHttpService } from '../../common/services/withdrawFunds-http.service';
import { WithdrawFund } from 'src/app/shared/interfaces/withdraw-fund-response.interface';
import { InvestmentsHttpService } from 'src/app/investments/common/services/investments-http.service';
import Swal from 'sweetalert2';
import * as moment from 'moment';
import { INVESTOR_ID } from 'src/app/shared/enums/common.enum';
import { InvestorsByIdResponseInterface } from 'src/app/shared/interfaces/Investors-response.interface';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { InvestmentsResponseInterface } from 'src/app/shared/interfaces/investments-response.interface';
import { BankDetailsHttpService } from "src/app/bank-details/services/bankDetails-http.service";
import { Router } from "@angular/router";
import {
  FinancialStatementHttpService
} from "src/app/financial-statement/common/services/financial-statement-http.service";
import { SpinnerService } from "../../../shared/services/spinner.service";
import { MasterDataHttpService } from "../../../shared/services/master-data-http.service";
import { CCY_SORT_ORDER } from "../../../shared/constants/helpers.const";
import { SimpleNumberValueFormatter } from "../../../shared/helpers/number-formatter.helper";
import { formatSimpleNumber } from "../../../shared/helpers/v2";

@Component({
  selector: "app-withdraw-funds",
  templateUrl: "./withdraw-funds.component.html",
  styleUrls: ["./withdraw-funds.component.scss"]
})
export class WithdrawFundsComponent implements OnInit {
  withdrawFundForm: FormGroup;
  isSubmitted = false;
  noteText = "These funds will be remitted to:";
  iban = "IBAN";
  private changeSubscription: Subscription = new Subscription();
  type: { id: string; name: string }[];
  withdrawFundData: WithdrawFund;
  investors: { id: string; name: string }[];
  product: { id: string; name: string }[];
  allCcyData: { id: string; name: string }[];
  investorId: string;
  financialStatementData: any[] = [];
  currentDate = moment().format("YYYY-MM-DD");
  private queryParams: Partial<any> = {
    pageIndex: 0,
    perPage: 10000,
    investorId: INVESTOR_ID
  };
  isUrlExists = false;
  today = new Date();

  constructor(
    private withdrawFundsHttpService: WithdrawFundsHttpService,
    private investmentsHttpService: InvestmentsHttpService,
    private withdrawFundsFormProvider: WithdrawFundsFormProvider,
    public activeModal: NgbActiveModal,
    private config: NgbDatepickerConfig,
    private router: Router,
    private bankDetailsHttpService: BankDetailsHttpService,
    private financialStatementHttpService: FinancialStatementHttpService,
    private spinnerService: SpinnerService,
    private masterDataHttpService: MasterDataHttpService,
  ) {
    this.initDatePickerConfig();
    this.initializeIntermediaryId();
  }

  ngOnInit(): void {
    const path = "/financial-statement";
    const exists = this.router.config.some(route => route.path === path);
    this.getFinancialStatementByInvestorId(this.generateRequestList());
    this.withdrawFundForm = this.withdrawFundsFormProvider.initWithdrawFundsForm(this.withdrawFundData);
    if (this.investorId) {
      this.withdrawFundForm.get("investorId")?.setValue(this.investorId);
      this.getInvestmentsById(this.investorId);
      if (exists) {
        this.isUrlExists = true;
      }
    }
    this.getDropDownsData();
    this.getBankAccounts();
  }

  initializeIntermediaryId(): void {
    const storedData = localStorage.getItem("userData");
    const userDetails = storedData ? JSON.parse(storedData) : {};
    this.investorId = userDetails.entityRoleId || null;
  }

  onSubmit() {
    this.isSubmitted = true;
    if (this.withdrawFundForm.invalid) {
      return;
    }
    let rawData = this.withdrawFundForm.value;
    rawData = getFormData(rawData);
    rawData.investorId = this.investorId.toString();
    rawData.faceValue = Number(rawData.faceValue.replace(/,/g, ""));
    delete rawData.intermediaryName;
    this.doCreate(rawData);
  }

  onSelect(event: { id: string; name: string }): void {
    if (event?.id) {
      this.getInvestorById(event.id);
    } else {
      this.iban = "IBAN";
    }
  }

  private getBankAccounts(): void {
    this.bankDetailsHttpService.getBankAccounts().subscribe({
      next: (response: any) => {
        if (response?.length) {
          this.iban = response?.[0]?.iban;
        }
      }
    });
  }

  doCreate(data: any): void {
    this.spinnerService.setShowSpinner(true);
    this.investmentsHttpService.postData("investments", data).subscribe({
      next: (response: any) => {
        this.closeModal();
        this.spinnerService.setShowSpinner(false);
        this.withdrawFundForm.reset();
        Swal.fire({
          icon: "success",
          text: "Withdrawal completed successfully.",
          showConfirmButton: false,
          timer: 5000
        });
      },
      error: (error) => {
        this.spinnerService.setShowSpinner(false);
        let errorMessages = error.error.message;
        if (typeof errorMessages === "string") {
          errorMessages = [errorMessages];
        }
        const containsNoReceiptError = errorMessages.some((msg: string | string[]) => msg.includes("There is no receipt investment"));
        const containsFutureDateError = errorMessages.some((msg: string | string[]) => msg.includes("maximal allowed date for"));
        const defaultMessage = containsNoReceiptError || containsFutureDateError
          ? "Error"
          : errorMessages.join(", ");
        Swal.fire({
          icon: "error",
          text: defaultMessage,
          showConfirmButton: true,
          confirmButtonText: "Ok"
        });
      }
    });
  }

  private getDropDownsData(): void {
    this.withdrawFundsHttpService.getFilterData().subscribe((filterData) => {
      filterData.types.sort((a, b) => a.localeCompare(b));
      //Type
      this.type = [
        ...(filterData.types.map((type) => ({
          id: type,
          name: type
        })) || [])
      ];

      // Product
      this.product = [
        ...(filterData.products.map((product) => ({
          id: product,
          name: product
        })) || [])
      ];

      let foundInvestor: any = null;
      let foundIntermediary: any = null;
      for (let intermediary of filterData.intermediariesWithInvestors) {
        let investor = intermediary.investors.find((investor: any) => investor.id == this.investorId);
        if (investor) {
          foundInvestor = investor;
          foundIntermediary = intermediary;
          break;
        }
      }
      if (!this.investors) {
        this.investors = [];
      }

      if (foundInvestor && foundIntermediary) {
        this.investors.push({
          id: foundIntermediary.id,
          name: foundIntermediary.name
        });
      }

      this.investors.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      this.withdrawFundForm.get("intermediaryId")?.setValue(this.investors[0]?.id);
      this.withdrawFundForm.get("intermediaryName")?.setValue(this.investors[0]?.name);
    });


    this.masterDataHttpService.getCurrencyList()
      .subscribe((response: any) => {
        let orderMap: any = new Map(CCY_SORT_ORDER.map((ccy, index) => [ccy, index]));
        this.allCcyData = response
          .sort((a: any, b: any) => orderMap.get(a) - orderMap.get(b))
          .map((item: any) => {
            return { id: item, name: item };
          });
      })
  }

  getInvestorById(id: string) {
    this.investmentsHttpService.getInvestorById(id).subscribe({
      next: (response: InvestorsByIdResponseInterface) => {
        if (response.bankAccounts && response.bankAccounts.length > 0) {
          this.iban = response.bankAccounts[0].iban;
        } else {
          this.iban = "IBAN";
          Swal.fire({
            icon: "error",
            text: "No IBAN found for the investor.",
            showConfirmButton: true,
            confirmButtonText: "Ok"
          });
        }
      }
    });
  }

  closeModal() {
    this.activeModal.dismiss();
  }

  private getCurrentDate(): NgbDate {
    const today = new Date();
    return new NgbDate(today.getFullYear(), today.getMonth() + 1, today.getDate());
  }

  initDatePickerConfig() {
    const currentDate = this.getCurrentDate();
    this.config.minDate = { year: 1970, month: 1, day: 1 };
    this.config.maxDate = currentDate;
  }

  getInvestmentsById(investorId: string) {
    const queryParams = {
      perPage: 10000,
      investorId: investorId
    };
    this.investmentsHttpService.getInvestmentsData(queryParams).subscribe({
      next: (response: InvestmentsResponseInterface) => {
        this.withdrawFundForm.get("product")?.setValue(
          this.getUniqueValue(response.items, "product") === "Multiple" ? "" : this.getUniqueValue(response.items, "product")
        );
      }
    });
  }

  setPrePopulatedValue() {
    const data = localStorage.getItem("financialStatementData");
    if (data) {
      this.financialStatementData = JSON.parse(data);
      if (this.financialStatementData && this.financialStatementData.length > 0) {
        this.withdrawFundForm.get("date")?.setValue(this.currentDate);
        const ccy = this.getUniqueValue(this.financialStatementData, "ccy") ?? "";
        const type = this.getUniqueValue(this.financialStatementData, "type") ?? "";
        if (type === "Multiple") {
          this.withdrawFundForm.get("type")?.setValue("");
        } else {
          const months = type === "Demand" || type === "Fixed" ? 12 : type === "Term" ? 36 : null;
          if (months !== null) this.withdrawFundForm.get("months")?.setValue(months);
          this.withdrawFundForm.get("type")?.setValue(type);
        }
        this.withdrawFundForm.get("ccy")?.setValue(ccy === "Multiple" ? "" : ccy);
      }
    }
  }

  private getUniqueValue(data: any[], key: string) {
    const uniqueValues = [...new Set(data.map((item) => item[key]))];
    return uniqueValues.length > 1 ? "Multiple" : uniqueValues[0];
  }

  private generateRequestList(): Record<string, Observable<any>> {
    let requestList: Record<string, Observable<any>> = {};
    requestList = {
      demand: this.financialStatementHttpService.getFinancialStatementData({
        ...this.queryParams,
        perPage: 10000,
        type: "Demand"
      }),
      fixed: this.financialStatementHttpService.getFinancialStatementData({
        ...this.queryParams,
        perPage: 10000,
        type: "Fixed"
      }),
      term: this.financialStatementHttpService.getFinancialStatementData({
        ...this.queryParams,
        perPage: 10000,
        type: "Term"
      })
    };
    return requestList;
  }

  private getFinancialStatementByInvestorId(requestList: Record<string, Observable<any>>): void {
    forkJoin(requestList).subscribe((data) => {
      let rowData: any = [];
      const path = "/financial-statement";
      const exists = this.router.config.some(route => route.path === path);
      for (const dataKey in data) {
        switch (dataKey) {
          case "demand":
          case "fixed":
          case "term":
            rowData = [
              ...rowData,
              ...this.filterReceiptAmountsGrid(data[dataKey].items)
            ];
            break;
        }
      }
      localStorage.setItem("financialStatementData", JSON.stringify(rowData));
      if (this.investorId && rowData?.length) {
        this.withdrawFundForm.get("investorId")?.setValue(this.investorId);
        if (exists) {
          this.isUrlExists = true;
        }
        this.setPrePopulatedValue();
      }

    });
  }

  handleFocusFaceValue(): void {
    if (isNaN(+this.withdrawFundForm.value.faceValue)) {
      this.withdrawFundForm.get('faceValue')?.patchValue('')
      return;
    }
    this.withdrawFundForm.get('faceValue')?.patchValue(ConvertISONumberToNormal(+this.withdrawFundForm.value.faceValue));
  }

  handleFocusOutFaceValue(): void {
    if (isNaN(+this.withdrawFundForm.value.faceValue)) {
      this.withdrawFundForm.get('faceValue')?.patchValue('');
      return;
    }
    this.withdrawFundForm.get('faceValue')?.patchValue(formatSimpleNumber(this.withdrawFundForm.value.faceValue));
  }


  filterReceiptAmountsGrid(data: any[]): any[] {
    const filteredData: any[] = [];
    const seenGroups = new Map<string, any[]>();
    // Group data by type and currency
    data.forEach(item => {
      const key = `${item.type}-${item.ccy}`;
      if (!seenGroups.has(key)) {
        seenGroups.set(key, []);
      }
      seenGroups.get(key)?.push(item);
    });
    // Process each group
    seenGroups.forEach(entries => {
      let lastZeroIndex = -1;
      // Find the last index where balance is 0 or null
      for (let i = 0; i < entries.length; i++) {
        if (entries[i].balance === 0 || entries[i].balance === null) {
          lastZeroIndex = i;
        }
      }
      // Include only rows after the last zero/null balance
      if (lastZeroIndex !== -1 && lastZeroIndex < entries.length - 1) {
        filteredData.push(...entries.slice(lastZeroIndex + 1));
      } else if (lastZeroIndex === -1) {
        // If no zero/null balance found, include all rows
        filteredData.push(...entries);
      }
    });
    return filteredData;
  }
}
